import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Brushaway, BrushawayBody, BrushawayFooter, BrushawayHeader } from '@ui/molecules/Brushaway';
import { Button } from '@ui/atoms/Button';
import { Text } from '@ui/atoms/Text';
import { Loader } from '@ui/atoms/Loader';
import { ConfirmDialog } from '@ui/molecules/ConfirmDialog';
import { InlineNotification } from '@ui/molecules/InlineNotification';
import { TextInput } from '@ui/molecules/inputs/form/TextInput';
import { SectionHeader } from '@ui/molecules/Header/SectionHeader';

import { t } from '../constants';
import { RENAME_PARAM_KEY } from '../services/renameParams';
import useTagDetail from '../hooks/useTagDetail';
import TagUsageTable from './TagUsageTable';

// IP-15330: the tag detail, as a Brushaway (Storybook: Molecules/Brushaway) over
// the listing rather than the full page it used to be.
//
// Three concerns, deliberately kept apart because their blast radius differs:
//   1. Rename  — global by construction (tags.name is uniquely indexed)
//   2. Remove from a single type — detaches that type's taggings only
//   3. Delete everywhere — removes the tag and every tagging of it
function TagDrawer({ tagId, taggableTypeLabels, onMutated, onClose }) {
  const [pendingType, setPendingType] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const { tag, name, setName, loading, saving, errors, rename, detach, destroy } = useTagDetail({
    tagId,
    onMutated,
    onClose
  });

  const total = tag?.total ?? 0;
  const busy = loading || saving;

  return (
    <>
      <Brushaway controlledOpen={tagId !== null} onClose={onClose} minWidth={640}>
        <BrushawayHeader title={t('drawer.title', { name: tag?.name ?? '' })} />

        <BrushawayBody>
          {/* BrushawayBody supplies 2.4rem padding but no gap, and SectionHeader
              carries no vertical margin, so consecutive blocks stacked flush against
              each other. Laid out as a column on the 2.4rem rhythm the rest of the
              app uses (workspace__section), with the usage heading held closer to the
              table it labels so the two read as one group. Inline rather than a new
              class — the design system has no stacked-body utility to reuse. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2.4rem' }}>
            {errors.length > 0 && <InlineNotification variant="error">{errors.join(' ')}</InlineNotification>}

            {loading && !tag && <Loader />}

            {tag && (
              <>
                <Text variant="body" as="p" style={{ maxWidth: '700px' }}>
                  {t('drawer.global_warning', { count: total })}
                </Text>

                <TextInput
                  id="tag-name"
                  // Matches Settings::TagsController#tag_params — see RENAME_PARAM_KEY.
                  name={`${RENAME_PARAM_KEY}[name]`}
                  label={t('drawer.name_label')}
                  required
                  value={name}
                  disabled={busy}
                  onChange={(event) => setName(event.target.value)}
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                  <SectionHeader title={t('drawer.usage.heading')} />

                  <TagUsageTable
                    countsByType={tag.countsByType}
                    taggableTypeLabels={taggableTypeLabels}
                    onRemove={setPendingType}
                    disabled={busy}
                  />

                  {total > 0 && (
                    <Text variant="body" as="p" style={{ maxWidth: '700px' }}>
                      {t('drawer.usage.remove_note')}
                    </Text>
                  )}
                </div>

                {/* No `title` — Product: the bare warning text, not a "Delete" heading
                    stacked above it. */}
                <InlineNotification variant="warning">
                  {t('drawer.delete.warning', { name: tag.name, count: total })}
                </InlineNotification>
              </>
            )}
          </div>
        </BrushawayBody>

        <BrushawayFooter
          startActions={
            tag && (
              <Button variant="danger" disabled={busy} onClick={() => setConfirmingDelete(true)}>
                <i className="fa-light fa-trash" aria-hidden="true" />
                {` ${t('drawer.delete.submit')}`}
              </Button>
            )
          }>
          <Button variant="secondary" disabled={saving} onClick={onClose}>
            {t('drawer.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={busy || !tag || name.trim() === '' || name.trim() === tag.name}
            onClick={() => {
              // eslint-disable-next-line no-void
              void rename();
            }}>
            {t('drawer.save')}
          </Button>
        </BrushawayFooter>
      </Brushaway>

      <ConfirmDialog
        isOpen={pendingType !== null}
        title={t('drawer.usage.remove_confirm_title')}
        description={t('drawer.usage.remove_confirm', {
          name: tag?.name ?? '',
          type: taggableTypeLabels[pendingType] || pendingType || '',
          count: tag?.countsByType?.[pendingType] ?? 0
        })}
        confirmLabel={t('drawer.usage.remove')}
        confirmVariant="danger"
        loading={saving}
        onConfirm={() => {
          const taggableType = pendingType;
          setPendingType(null);
          // eslint-disable-next-line no-void
          void detach(taggableType);
        }}
        onCancel={() => setPendingType(null)}
      />

      <ConfirmDialog
        isOpen={confirmingDelete}
        title={t('drawer.delete.confirm_title')}
        description={t('drawer.delete.confirm', { name: tag?.name ?? '', count: total })}
        confirmLabel={t('drawer.delete.submit')}
        confirmVariant="danger"
        loading={saving}
        onConfirm={() => {
          setConfirmingDelete(false);
          // eslint-disable-next-line no-void
          void destroy();
        }}
        onCancel={() => setConfirmingDelete(false)}
      />
    </>
  );
}

TagDrawer.propTypes = {
  tagId: PropTypes.number,
  taggableTypeLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  onMutated: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

TagDrawer.defaultProps = {
  tagId: null
};

export default TagDrawer;
